import SatelliteAltOutlinedIcon from '@mui/icons-material/SatelliteAltOutlined';
import CellTowerIcon from '@mui/icons-material/CellTower';
import LockIcon from '@mui/icons-material/Lock';

import { SettingsState } from '../../redux/slices/settingsSlice';

import { secondsToDhms } from '../../utils/index';

import { LocationMehod, ReaderOwnerAccountId } from '../../constants/Constants';
import { ColumnExtendedStrict } from '../../types/ColumnExtended';
import { Reader } from '../../types/Reader';

import DisplayLabels from '../../components/pages/labels/DisplayLabels';

import { dateHeader, formatCellDate } from './timeModeCell';
import { linkTo } from '../linkTo';

export const makeReaderListColumn = (settings: SettingsState) => {
  const { opsStatusColor, supplyChainStatusColor } = settings;

  const getOpsStatusColor = (statusId) => opsStatusColor?.value.find((v) => v.text === statusId)?.value ?? '';
  const getSupplyChainStatusColor = (statusId) => supplyChainStatusColor?.value.find((v) => v.text === statusId)?.value ?? '';

  const renderLocationMethod = (locationMethod) => {
    return locationMethod === LocationMehod.GPS ? (
      <SatelliteAltOutlinedIcon style={{ color: 'green', fontSize: '1.1rem' }} />
    ) : locationMethod === LocationMehod.NETWORK ? (
      <CellTowerIcon style={{ color: 'blue', fontSize: '1.1rem' }} />
    ) : (
      <></>
    );
  };

  const ownershipMap = Object.entries(ReaderOwnerAccountId).reduce(
    (acc, [key, value]) => {
      const readableKey = key.replace(/([A-Z])/g, ' $1').trim();
      acc[value as string] = readableKey;
      return acc;
    },
    {} as Record<string, string>,
  );

  const col: ColumnExtendedStrict<Reader>[] = [
    {
      header: 'Imei',
      accessorKey: 'imei',
      enableColumnFilter: true,
      size: 140,
      cell: ({ row }) => linkTo('/readers', row.original.imei, row.original.imei),
    },
    {
      header: 'Name',
      accessorKey: 'name',
      enableColumnFilter: true,
      size: 650,
      cell: ({ row }) => <span title={row.original.name}>{row.original.name}</span>,
    },
    {
      header: 'Country',
      accessorKey: 'isoCountryCode',
      enableColumnFilter: true,
      size: 60,
    },
    {
      header: 'Region',
      accessorKey: 'isoRegionCode',
      enableColumnFilter: true,
      size: 125,
    },
    {
      header: 'Ownership',
      accessorKey: 'accountId',
      enableColumnFilter: true,
      size: 125,
      cell: ({ row }) => {
        const accountId = row.original.accountId ?? '';
        const ownershipLabel = ownershipMap[accountId];
        return <span title={accountId}>{ownershipLabel}</span>;
      },
    },
    {
      header: 'Battery',
      accessorKey: 'batteryPercent',
      size: 60,
      cell: ({ row }) => {
        const percentage = row.original.batteryPercent ?? 0;
        const color = percentage >= 80 ? 'green' : percentage <= 20 ? 'red' : percentage <= 30 ? 'orange' : '';
        return <span style={{ color: color, textAlign: 'center' }}>{percentage}%</span>;
      },
    },
    {
      header: 'Interval',
      accessorKey: 'reportInterval',
      size: 60,
      cell: ({ row }) => <span style={{ textAlign: 'center' }}> {secondsToDhms(row.original.reportInterval)}</span>,
    },
    {
      header: 'Method',
      accessorKey: 'locationMethod',
      size: 60,
      cell: ({ row }) => (
        <span style={{ textAlign: 'center' }} title={row.original.locationMethodDescr}>
          {renderLocationMethod(row.original.locationMethod)}
        </span>
      ),
    },
    {
      header: 'Mobile',
      accessorKey: 'isMobile',
      size: 55,
      cell: ({ row }) => <span style={{ textAlign: 'center' }}>{row.original.isMobile ? 'Yes' : 'No'}</span>,
    },
    {
      header: 'Locked',
      accessorKey: 'lockedLocation',
      size: 55,
      cell: ({ row }) => row.original.lockedLocation && <LockIcon style={{ color: 'green', fontSize: '1.1rem', textAlign: 'center' }} />,
    },
    {
      header: 'Use',
      accessorKey: 'intendedUse',
      size: 55,
      cell: ({ row }) => <span style={{ textAlign: 'center' }}>{row.original.intendedUseDescr}</span>,
    },
    {
      header: 'On Airport',
      accessorKey: 'isOnAirport',
      size: 70,
      cell: ({ row }) => <span style={{ textAlign: 'center' }}>{row.original.isOnAirport ? 'Yes' : 'No'}</span>,
    },
    {
      header: 'Station',
      accessorKey: 'stationIataCode',
      enableColumnFilter: true,
      size: 50,
      cell: ({ row }) => <span style={{ textAlign: 'center' }}>{row.original.stationIataCode}</span>,
    },

    {
      header: 'Ops Status',
      accessorKey: 'opsStatus',
      size: 90,
      cell: ({ row }) => <span style={{ color: getOpsStatusColor(row.original.opsStatus), textAlign: 'center' }}>{row.original.opsStatusDescr}</span>,
    },
    {
      header: 'Supply Chain',
      accessorKey: 'supplyChainStatus',
      enableSorting: true,
      size: 110,
      cell: ({ row }) => (
        <span style={{ color: getSupplyChainStatusColor(row.original.supplyChainStatus), textAlign: 'center' }}>
          {row.original.supplyChainStatusDescr}
        </span>
      ),
    },
    {
      header: dateHeader('Last Seen'),
      accessorKey: 'lastSeenDateTime',
      size: 110,
      cell: ({ row, table }) => formatCellDate(table, row.original.lastSeenDateTime),
    },
    {
      header: dateHeader('Created'),
      accessorKey: 'createdDateTime',
      size: 110,
      cell: ({ row, table }) => formatCellDate(table, row.original.createdDateTime),
    },
    {
      header: dateHeader('Modified'),
      accessorKey: 'modifiedDateTime',
      size: 110,
      cell: ({ row, table }) => formatCellDate(table, row.original.modifiedDateTime),
    },
    {
      header: 'Labels',
      accessorKey: 'labels',
      enableSorting: true,
      enableColumnFilter: true,
      size: 100,
      cell: ({ row }) => <DisplayLabels labels={row.original.labels} />,
    },
  ];

  return col;
};
